import { gql } from "@apollo/client";
import { Classes, Intent } from "@blueprintjs/core";
import { NotificationStatus, NotificationType } from "@hex/common";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import { HexButton } from "../../hex-components/HexButton.js";
import { HexTooltip } from "../../hex-components/HexTooltip.js";
import { useOrgExplorerRoleEnabled } from "../../hooks/me/useOrgExplorerRoleEnabled.js";
import { HumanDate } from "../common/HumanDate.js";
import { useToaster } from "../common/Toasts.js";
import { useFeatureGates } from "../feature-gate/FeatureGateContext.js";
import {
  NotificationReadIcon,
  NotificationUnreadIcon,
} from "../icons/CustomIcons.js";
import { UserAvatar } from "../user/UserAvatar.js";

import { getNotificationTitle } from "./getNotificationTitle.js";
import { NotificationTypeToIcon } from "./NotificationIcons.js";
import {
  NotificationRowFragment,
  useHandleNotificationActionMutation,
} from "./NotificationRow.generated.js";
import { UnreadIndicator } from "./UnreadIndicator.js";

gql`
  mutation HandleNotificationAction(
    $notificationUserLinkId: NotificationUserLinkId!
  ) {
    handleNotificationAction(notificationUserLinkId: $notificationUserLinkId) {
      id
    }
  }

  fragment NotificationRowFragment on NotificationUserLink {
    id
    type
    contents
    createdDate
    status
    fromUser {
      id
      name
      email
      imageUrl
    }
  }
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

interface NotificationRowProps {
  notification: NotificationRowFragment;
  toggleNotificationStatus: () => void;
}

export const NotificationRow: React.ComponentType<NotificationRowProps> =
  React.memo(function NotificationRow({
    notification,
    toggleNotificationStatus,
  }) {
    const isRead = notification.status === NotificationStatus.READ;

    return (
      <RowContainer>
        <UnreadIndicator
          css={`
            position: absolute;
            left: 16px;
            top: 0px;
          `}
          isRead={isRead}
        />
        <NotificationBody
          notification={notification}
          toggleNotificationStatus={toggleNotificationStatus}
        />
      </RowContainer>
    );
  });

const Title = styled.div<{ $longer?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};

  /* TODO(DES-1060): do not pattern match, prefer line-clamp */
  display: -webkit-box;
  -webkit-line-clamp: ${({ $longer }) => ($longer ? 3 : 2)};

  overflow: hidden;
  -webkit-box-orient: vertical;

  text-overflow: ellipsis;
`;

const Timestamp = styled.div`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.fontColor.MUTED};
  white-space: nowrap;
`;

const Body = styled.div`
  color: ${({ theme }) => theme.fontColor.MUTED};

  /* TODO(DES-1060): do not pattern match, prefer line-clamp */
  display: -webkit-box;
  -webkit-line-clamp: 3;

  overflow: hidden;
  -webkit-box-orient: vertical;

  text-overflow: ellipsis;
`;

export const RightActionWrapper = styled.div`
  margin-left: auto;
  opacity: 0;

  .${Classes.POPOVER_TARGET} {
    display: flex;
  }
`;

const IconWrapper = styled.div`
  padding-top: 2px;
  .${Classes.ICON} {
    /* reseting this so that spacing is consistent for icons/user avatars */
    vertical-align: unset;
  }
`;

interface NotificationBodyProps {
  notification: NotificationRowFragment;
  toggleNotificationStatus: () => void;
}

const NotificationBody: React.ComponentType<NotificationBodyProps> = React.memo(
  function NotificationBody({ notification, toggleNotificationStatus }) {
    const { body, link, titleMetadata } = notification.contents;
    const user = notification.fromUser;
    const displayName = user?.name || user?.email || "Unknown User";

    const createdDate = notification.createdDate;

    const { explorerRole: explorerRoleGate } = useFeatureGates();
    const explorerRoleEnabled = useOrgExplorerRoleEnabled();
    const exploreRoleVisibleButDisabled =
      !explorerRoleEnabled && explorerRoleGate;
    const allowLongerMessage =
      notification.type === "ORG_ROLE_REQUEST" && exploreRoleVisibleButDisabled;

    const title = getNotificationTitle({
      displayName,
      exploreRoleVisibleButDisabled,
      link,
      titleMetadata,
    });

    const toaster = useToaster();
    const [handleNotification] = useHandleNotificationActionMutation();

    const actionNotificationCallback = useCallback(
      (event: React.MouseEvent, successString: string, errorString: string) => {
        // Prevent the notification from being clicked and opening the link
        event.preventDefault();
        event.stopPropagation();

        handleNotification({
          variables: {
            notificationUserLinkId: notification.id,
          },
          update: (cache, { data }) => {
            if (!data?.handleNotificationAction) return;
            // Update the notification in the cache with resolved status
            cache.modify({
              id: cache.identify({
                __typename: "NotificationUserLink",
                id: notification.id,
              }),
              fields: {
                contents: (existingContents) => ({
                  ...existingContents,
                  titleMetadata: {
                    ...existingContents.titleMetadata,
                    isResolved: true,
                  },
                }),
                status: (_) => NotificationStatus.READ,
              },
            });
          },
        })
          .then(() => {
            toaster.show({
              message: successString,
              intent: Intent.SUCCESS,
            });
          })
          .catch((error) => {
            toaster.show({
              message: errorString,
              intent: Intent.DANGER,
            });
            console.error(error);
          });
      },
      [handleNotification, notification.id, toaster],
    );

    const CTA = useMemo(() => {
      if (
        titleMetadata.type === NotificationType.HEX_ROLE_REQUEST &&
        !titleMetadata.isResolved
      ) {
        return (
          <HexButton
            intent={Intent.SUCCESS}
            small={true}
            subtle={true}
            // eslint-disable-next-line react/jsx-no-bind -- passing event
            onClick={(event) =>
              actionNotificationCallback(
                event,
                "Access granted",
                "Error granting access",
              )
            }
          >
            Grant {displayName} access
          </HexButton>
        );
      }
      return null;
    }, [actionNotificationCallback, displayName, titleMetadata]);

    const icon = useMemo(() => {
      if (user) {
        return (
          <UserAvatar
            email={user?.email}
            imageUrl={user?.imageUrl ?? undefined}
            name={user?.name ?? undefined}
            size={16}
          />
        );
      } else {
        return <>{NotificationTypeToIcon[notification.type]}</>;
      }
    }, [notification.type, user]);

    const onToggle = useCallback(
      (evt: React.MouseEvent) => {
        // don't propagate this to click on the parent menu item (which would
        // navigate to the notification link)
        evt.stopPropagation();
        void toggleNotificationStatus();
      },
      [toggleNotificationStatus],
    );

    return (
      <div
        css={`
          display: flex;
          gap: 10px;
          width: 100%;
        `}
      >
        <IconWrapper>{icon}</IconWrapper>
        <div
          css={`
            display: flex;
            flex-direction: column;
            gap: 2px;
            width: 100%;
          `}
        >
          <div
            css={`
              display: flex;
              gap: 4px;
              align-items: start;
            `}
          >
            <Title $longer={allowLongerMessage}>{title}</Title>
            <RightActionWrapper>
              <HexTooltip
                content={
                  notification.status === NotificationStatus.READ
                    ? "Mark as unread"
                    : "Mark as read"
                }
                position="bottom"
              >
                <HexButton
                  extraSmall={true}
                  icon={
                    notification.status === NotificationStatus.READ ? (
                      <NotificationUnreadIcon />
                    ) : (
                      <NotificationReadIcon />
                    )
                  }
                  minimal={true}
                  onClick={onToggle}
                />
              </HexTooltip>
            </RightActionWrapper>
          </div>
          {body && <Body>{body}</Body>}
          <div
            css={`
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <Timestamp>
              <HumanDate date={createdDate} />
            </Timestamp>
            {CTA}
          </div>
        </div>
      </div>
    );
  },
);
